import CloseIcon from "@mui/icons-material/CloseRounded";
import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import type { Theme } from "@mui/material";
import {
  Box,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useRef } from "react";
import documentEventsApi from "../../api/clients/documentEventsApi";
import useIsUserActive from "../../hooks/useIsUserActive";
import { logError } from "../../logging";
import { downloadFileFromUrl } from "../../services/downloadFile";
import { printBlob } from "../../utilities/printHelper";
import usePageViewTracking from "./pdfViewer/usePageViewTracking";

interface Props {
  src: string;
  title: string;
  documentViewSessionId?: string;
  preventDownload?: boolean;
  onClose: () => void;
}

const sendDownloadEvent = async (documentViewSessionId: string) => {
  try {
    await documentEventsApi.ingestDocumentDownloadEvent({
      viewEventsData: [
        {
          sessionId: documentViewSessionId,
          eventData: { download: true },
        },
      ],
    });
  } catch (error) {
    logError(error, "[PreviewImageDialog] ingestDocumentDownloadEvent");
  }
};

const PreviewImageDialog = ({ src, title, documentViewSessionId, preventDownload, onClose }: Props) => {
  const imageRef = useRef<HTMLImageElement>(null);

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const isUserActive = useIsUserActive();

  usePageViewTracking({
    sessionId: documentViewSessionId,
    numPages: 1,
    visiblePages: [1],
    pauseTracking: !isUserActive,
  });

  const handleDownloadClick = async () => {
    await downloadFileFromUrl(src);

    if (documentViewSessionId) {
      await sendDownloadEvent(documentViewSessionId);
    }
  };

  const handlePrintClick = async () => {
    try {
      const blobResp = await fetch(src);
      if (!blobResp.ok) {
        logError(blobResp.statusText, "[PreviewImageDialog] download image blob");
        return;
      }

      const blob = await blobResp.blob();
      await printBlob(blob);
    } catch (error) {
      logError(error, "[PreviewImageDialog] handlePrintClick");
    }
  };

  const handleClose = () => {
    onClose();

    if (imageRef.current) {
      imageRef.current.src = "";
    }
  };

  return (
    <Dialog
      open={Boolean(src)}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isSmallScreen}
      slotProps={isSmallScreen ? undefined : { paper: { sx: { minWidth: "20rem", minHeight: "20rem" } } }}
    >
      <DialogTitle sx={{ px: 2, py: 1 }}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography component="span" variant="h6" noWrap>
            {title}
          </Typography>
          <Stack direction="row" spacing={1}>
            {!preventDownload && (
              <>
                <IconButton onClick={handleDownloadClick}>
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={handlePrintClick}>
                  <PrintIcon />
                </IconButton>
              </>
            )}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Card sx={{ flexGrow: 1 }} variant="outlined">
            <CardMedia
              ref={imageRef}
              component="img"
              src={src}
              onContextMenu={(e) => e.preventDefault()}
              sx={{ objectFit: "contain", border: 0, height: "100%", width: "100%" }}
            />
          </Card>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewImageDialog;
